// Fee calculation utility based on the Agents Capital fee structure
// Option A = Brokerage Instruction (base rates)
// Option B = Solicitor Instruction (payout is 97% of Option A)
// Option C = Commission Loan (payout is 85% of Option A)
// Option D = Gazeal Agreement (flat 15% net fee, no duration band, no size adjustment)
//
// VAT: HMRC treat the AC fee as a supply of services. The net fee is grossed up
// by VAT_RATE before being deducted from the commission to derive the agent's offer.
//   net_fee   = commission × total_rate
//   vat       = net_fee × VAT_RATE
//   gross_fee = net_fee + vat
//   offer     = commission − gross_fee

interface FeeBand {
  acRate: number;
  investorRate: number;
}

// VAT applied to the Agents Capital fee (charged on the supply of services).
export const VAT_RATE = 0.20;

// Agents Capital VAT registration number.
export const VAT_REG_NUMBER = "483 4993 41";

// Option D (Gazeal) flat net fee rate, irrespective of duration or size.
export const OPTION_D_FLAT_RATE = 0.15;

// >= 2 months bands (duration in months) — these are the STANDARD (Option A) rates
// Rates are NET of VAT — VAT @ 20% is added on top in the calculation flow.
const STANDARD_BANDS: { maxMonths: number; band: FeeBand }[] = [
  { maxMonths: 2, band: { acRate: 0.058, investorRate: 0.029 } },
  { maxMonths: 3, band: { acRate: 0.067, investorRate: 0.029 } },
  { maxMonths: 4, band: { acRate: 0.058, investorRate: 0.048 } },
  { maxMonths: 5, band: { acRate: 0.067, investorRate: 0.048 } },
  { maxMonths: 6, band: { acRate: 0.077, investorRate: 0.067 } },
];

// < 2 months bands (duration in weeks)
const STANDARD_WEEKLY: { maxWeeks: number; band: FeeBand }[] = [
  { maxWeeks: 1, band: { acRate: 0.012, investorRate: 0.008 } },
  { maxWeeks: 2, band: { acRate: 0.024, investorRate: 0.012 } },
  { maxWeeks: 3, band: { acRate: 0.036, investorRate: 0.016 } },
  { maxWeeks: 4, band: { acRate: 0.036, investorRate: 0.020 } },
  { maxWeeks: 5, band: { acRate: 0.048, investorRate: 0.024 } },
  { maxWeeks: 6, band: { acRate: 0.060, investorRate: 0.028 } },
  { maxWeeks: 7, band: { acRate: 0.060, investorRate: 0.029 } },
  { maxWeeks: 8.57, band: { acRate: 0.067, investorRate: 0.029 } }, // 7 weeks to 2 months
];

const OPTION_B_PAYOUT_FACTOR = 0.97; // Option B payout = Option A payout × 0.97
const OPTION_C_PAYOUT_FACTOR = 0.85; // Option C payout = Option A payout × 0.85
// Option D no longer derives from Option A — see OPTION_D_FLAT_RATE above.

// Size-based fee adjustment: additional rate based on commission amount
export function getSizeAdjustment(commission: number): number {
  if (commission < 2500) return 0.024;
  if (commission < 5000) return 0.012;
  if (commission <= 20000) return 0;
  if (commission <= 35000) return 0.006;
  if (commission <= 50000) return 0.012;
  return 0.024;
}

// Option C purchase price caps based on agent's completed deal history
export function getOptionCCap(completedDealCount: number): number {
  if (completedDealCount === 0) return 10000;
  if (completedDealCount <= 3) return 15000;
  if (completedDealCount <= 6) return 25000;
  return 35000;
}

// Cap an Option C amount based on deal history
export function capOptionCAmount(amount: number, completedDealCount: number): number {
  const cap = getOptionCCap(completedDealCount);
  return Math.min(amount, cap);
}

// The fee period runs from the date funds are actually deployed:
// the exchange date when exchange is still in the future, otherwise today
// (the request date) when exchange has already happened.
export function resolveFeeStartDate(exchangeDate: string | Date | null): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!exchangeDate) return today;
  const exchange = new Date(exchangeDate);
  if (isNaN(exchange.getTime())) return today;
  exchange.setHours(0, 0, 0, 0);
  return exchange > today ? exchange : today;
}

function getDurationMonths(exchangeDate: string | null, completionDate: string | null): number {
  if (!completionDate) return 2; // default
  const start = resolveFeeStartDate(exchangeDate);
  const completion = new Date(completionDate);
  return Math.max(0, (completion.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
}

// A discount reduces our NET fee; VAT is then charged on the reduced fee.
// The agent therefore receives the discount plus the VAT saved on it.
export function applyDiscountToOffer(offerAmount: number, netDiscount: number): number {
  if (!netDiscount || netDiscount <= 0) return offerAmount;
  return offerAmount + netDiscount * (1 + VAT_RATE);
}

// Net fee (before VAT) implied by a stored offer amount.
export function netFeeFromOffer(commission: number, offerAmount: number): number {
  return Math.max(0, (commission - offerAmount) / (1 + VAT_RATE));
}

export function getDurationMonthsFromDates(startDate: Date, endDate: Date): number {
  return Math.max(0, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
}

function getStandardBand(durationMonths: number): FeeBand {
  const durationWeeks = durationMonths * (30.44 / 7);

  // < 2 months: use weekly bands
  if (durationMonths < 2) {
    for (const { maxWeeks, band } of STANDARD_WEEKLY) {
      if (durationWeeks <= maxWeeks) return band;
    }
  }

  for (const { maxMonths, band } of STANDARD_BANDS) {
    if (durationMonths <= maxMonths) return band;
  }

  // > 6 months: use the 5-6 month band as default
  return STANDARD_BANDS[STANDARD_BANDS.length - 1].band;
}

// For options B and C, we calculate differently:
// First compute Option A payout, then reduce it by the payout factor.
// The "fee" becomes the difference between commission and the reduced payout.
function getPayoutFactor(option: "a" | "b" | "c"): number {
  if (option === "a") return 1;
  if (option === "b") return OPTION_B_PAYOUT_FACTOR;
  return OPTION_C_PAYOUT_FACTOR;
}

export interface FeeResult {
  acRate: number;
  investorRate: number;
  totalRate: number;
  acFee: number;
  investorFee: number;
  totalFee: number;        // gross (VAT-inclusive) fee deducted from commission to derive offer
  netFee: number;          // fee before VAT
  vatAmount: number;       // VAT charged on the net fee
  grossFee: number;        // alias of totalFee, for clarity in UI breakdowns
  durationMonths: number;
  isCustom: boolean;
  sizeAdjustmentRate: number;
}

export function calculateFees(
  commission: number,
  exchangeDate: string | null,
  completionDate: string | null,
  option: "a" | "b" | "c" | "d" = "a",
  customAcRate?: number | null,
  customInvestorRate?: number | null,
): FeeResult {
  const durationMonths = getDurationMonths(exchangeDate, completionDate);
  const standardBand = getStandardBand(durationMonths);

  const isCustom = customAcRate != null || customInvestorRate != null;

  // Helper: assemble the final FeeResult by grossing up the net fee with VAT.
  const finalise = (
    netAcFee: number,
    netInvestorFee: number,
    sizeAdjustmentRate: number,
    isCustomFlag: boolean,
  ): FeeResult => {
    const netFee = netAcFee + netInvestorFee;
    const vatAmount = netFee * VAT_RATE;
    const grossFee = netFee + vatAmount;
    const netRate = commission > 0 ? netFee / commission : 0;
    return {
      acRate: commission > 0 ? netAcFee / commission : 0,
      investorRate: commission > 0 ? netInvestorFee / commission : 0,
      totalRate: netRate, // net rate (VAT shown separately in UI)
      acFee: netAcFee,
      investorFee: netInvestorFee,
      totalFee: grossFee, // gross — what gets deducted from commission to derive offer
      netFee,
      vatAmount,
      grossFee,
      durationMonths,
      isCustom: isCustomFlag,
      sizeAdjustmentRate,
    };
  };

  // Option D (Gazeal): flat 15%, no duration band, no size adjustment.
  if (option === "d" && !isCustom) {
    const netAcFee = commission * OPTION_D_FLAT_RATE;
    return finalise(netAcFee, 0, 0, false);
  }

  // Size adjustment is skipped when custom rates are set
  const sizeAdjustmentRate = isCustom ? 0 : getSizeAdjustment(commission);

  if (option === "a" || option === "d" || isCustom) {
    // Option A or custom rates (or custom override on D): straightforward rate-based calculation
    const acRate = customAcRate ?? standardBand.acRate;
    const investorRate = customInvestorRate ?? standardBand.investorRate;
    const netAcFee = commission * (acRate + sizeAdjustmentRate); // size adjustment goes to AC
    const netInvestorFee = commission * investorRate;
    return finalise(netAcFee, netInvestorFee, sizeAdjustmentRate, isCustom);
  }

  // Option B/C: compute Option A net payout first (including size adjustment),
  // then apply payout factor to derive the option's net fee.
  const optionARate = standardBand.acRate + standardBand.investorRate + sizeAdjustmentRate;
  const optionAPayout = commission - commission * optionARate;
  const payoutFactor = getPayoutFactor(option);
  const adjustedPayout = optionAPayout * payoutFactor;
  const netFee = commission - adjustedPayout;

  // Split fee proportionally between AC and investor based on original band ratio
  const bandTotal = standardBand.acRate + standardBand.investorRate;
  const acShare = bandTotal > 0 ? standardBand.acRate / bandTotal : 0.5;
  const netAcFee = netFee * acShare;
  const netInvestorFee = netFee * (1 - acShare);

  return finalise(netAcFee, netInvestorFee, sizeAdjustmentRate, false);
}

// Calculate the offer amount (what the agent receives) = commission - total fees
export function calculateOfferAmount(
  commission: number,
  exchangeDate: string | null,
  completionDate: string | null,
  option: "a" | "b" | "c" | "d" = "a",
): number {
  const fees = calculateFees(commission, exchangeDate, completionDate, option);
  return commission - fees.totalFee;
}

// Get the actual offer amount for a signed deal based on its selected option
export function getSelectedOfferAmount(deal: {
  selected_option: string | null;
  offer_a_amount: number | null;
  offer_b_amount: number | null;
  offer_c_amount?: number | null;
  offer_d_amount?: number | null;
}): number | null {
  switch (deal.selected_option) {
    case "a": return deal.offer_a_amount;
    case "b": return deal.offer_b_amount;
    case "c": return deal.offer_c_amount ?? null;
    case "d": return deal.offer_d_amount ?? null;
    default: return null;
  }
}

// Get actual financials for a signed deal using stored offer amounts
// Returns fee breakdown based on actual agreed amounts, not recalculated rates
export function getActualDealFinancials(deal: {
  commission_due: number | null;
  selected_option: string | null;
  offer_a_amount: number | null;
  offer_b_amount: number | null;
  offer_c_amount?: number | null;
  offer_d_amount?: number | null;
  exchange_date: string | null;
  completion_date: string | null;
  custom_ac_rate?: number | null;
  custom_investor_rate?: number | null;
}): { offerAmount: number; totalFee: number; acFee: number; investorFee: number; totalRate: number; acRate: number; investorRate: number; durationMonths: number; netFee: number; vatAmount: number; grossFee: number } | null {
  const commission = deal.commission_due || 0;
  if (!commission || !deal.selected_option) return null;

  const offerAmount = getSelectedOfferAmount(deal);
  if (offerAmount === null) return null;

  // Stored offer was calculated as commission − grossFee, where grossFee = netFee × (1 + VAT_RATE).
  const grossFee = commission - offerAmount;
  // Back out the net fee from the gross. For legacy deals saved before VAT was introduced
  // there is no clean way to distinguish — the gross-fee assumption gives a consistent split.
  const netFee = grossFee / (1 + VAT_RATE);
  const vatAmount = grossFee - netFee;

  // Use calculateFees to get the AC/investor net split ratio
  const option = (deal.selected_option === "a" || deal.selected_option === "b" || deal.selected_option === "c" || deal.selected_option === "d")
    ? deal.selected_option as "a" | "b" | "c" | "d"
    : "a" as const;
  const fees = calculateFees(commission, deal.exchange_date, deal.completion_date, option, deal.custom_ac_rate, deal.custom_investor_rate);

  // Split the actual NET fee proportionally based on the calculated AC/investor ratio.
  // VAT is collected by AC and remitted to HMRC — it does not count as AC earnings.
  const calcNet = fees.acFee + fees.investorFee;
  const acShare = calcNet > 0 ? fees.acFee / calcNet : 1;
  const acFee = netFee * acShare;
  const investorFee = netFee * (1 - acShare);

  return {
    offerAmount,
    totalFee: grossFee,
    acFee,
    investorFee,
    totalRate: commission > 0 ? netFee / commission : 0, // net rate
    acRate: commission > 0 ? acFee / commission : 0,
    investorRate: commission > 0 ? investorFee / commission : 0,
    durationMonths: fees.durationMonths,
    netFee,
    vatAmount,
    grossFee,
  };
}
